import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import { useLocation, useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import LandingFooter from '../nord-pass-landing-page/components/LandingFooter';

const LEGAL_PAGES = [
  { id: 'privacy', title: 'Personvernerklæring', excerpt: 'Informasjon om hvordan NordPass samler inn, bruker og beskytter dine personopplysninger i henhold til GDPR.', href: '/privacy-policy-personvern', category: 'Juridisk', tags: ['personvern', 'GDPR', 'data', 'cookies', 'sikkerhet'] },
  { id: 'terms', title: 'Vilkår & Betingelser', excerpt: 'Tjenestevilkår, abonnementsplaner, betalingsbetingelser og ansvarsfraskrivelser for bruk av NordPass-tjenester.', href: '/terms-conditions-vilk-r-betingelser', category: 'Juridisk', tags: ['vilkår', 'abonnement', 'betaling', 'ansvar', 'kontrakt'] }
];

const FAQ_DATA = [
  { id: 'faq-1', title: 'Hvordan oppretter jeg en konto?', excerpt: 'Registrer deg med e-post og passord på nordpassno.com. Velg abonnementsplan og bekreft e-postadressen din.', href: '/faq', category: 'FAQ', tags: ['konto', 'registrering', 'passord'] },
  { id: 'faq-2', title: 'Hva koster NordPass Premium?', excerpt: 'NordPass Premium koster 299 NOK per måned og inkluderer ubegrenset kjøretøyregistrering og prioritert kundestøtte.', href: '/faq', category: 'FAQ', tags: ['pris', 'premium', 'abonnement', 'kostnad'] },
  { id: 'faq-3', title: 'Hvordan beregnes bomavgifter?', excerpt: 'Bomavgifter beregnes basert på kjøretøytype, emisjonsklasse og tidspunkt for passering. Bruk vår bomkalkulator.', href: '/toll-calculator', category: 'FAQ', tags: ['bom', 'avgift', 'kalkulator', 'kjøretøy'] },
  { id: 'faq-4', title: 'Kan jeg kansellere abonnementet mitt?', excerpt: 'Ja, du kan kansellere abonnementet ditt når som helst fra Min konto-siden. Ingen bindingstid.', href: '/faq', category: 'FAQ', tags: ['kansellere', 'abonnement', 'oppsigelse'] }
];

const highlightText = (text, query) => {
  if (!query || !text) return text;
  const parts = text?.split(new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi'));
  return parts?.map((part, i) =>
    part?.toLowerCase() === query?.toLowerCase()
      ? <mark key={i} style={{ background: 'rgba(59,111,212,0.3)', color: '#A8C4F0', padding: '0 2px' }}>{part}</mark>
      : part
  );
};

const ResultCard = ({ result, query, type }) => {
  const typeColors = { Blog: '#3B6FD4', Juridisk: '#F97316', FAQ: '#2EC4A0' };
  const color = typeColors?.[type] || '#3B6FD4';

  return (
    <a href={result?.href} style={{ display: 'block', textDecoration: 'none', background: 'rgba(255,255,255,0.02)', border: `1px solid rgba(255,255,255,0.06)`, padding: '24px', marginBottom: '12px', transition: 'all 0.2s', position: 'relative', overflow: 'hidden' }}
      onMouseEnter={e => { e.currentTarget.style.borderColor = `${color}44`; e.currentTarget.style.background = `${color}06`; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.06)'; e.currentTarget.style.background = 'rgba(255,255,255,0.02)'; }}
    >
      <div style={{ position: 'absolute', top: 0, left: 0, width: '3px', height: '100%', background: color }} />
      <div style={{ paddingLeft: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px', flexWrap: 'wrap' }}>
          <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.6rem', color, letterSpacing: '0.15em', textTransform: 'uppercase', border: `1px solid ${color}44`, padding: '2px 8px' }}>{type}</span>
          {result?.category && result?.category !== type && <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.6rem', color: 'rgba(138,149,163,0.5)', letterSpacing: '0.1em' }}>{result?.category}</span>}
          {result?.published_at && <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.6rem', color: 'rgba(138,149,163,0.4)', letterSpacing: '0.05em' }}>{new Date(result?.published_at)?.toLocaleDateString('nb-NO')}</span>}
        </div>
        <h3 style={{ fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: '1.0625rem', color: '#EEF0F2', margin: '0 0 8px', lineHeight: 1.4 }}>{highlightText(result?.title, query)}</h3>
        <p style={{ fontFamily: 'Georgia, serif', fontStyle: 'italic', fontSize: '0.875rem', color: 'rgba(238,240,242,0.6)', lineHeight: 1.7, margin: '0 0 12px' }}>{highlightText(result?.excerpt || result?.content?.slice(0, 160) + '...', query)}</p>
        {result?.author_name && <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '0.75rem', color: 'rgba(138,149,163,0.5)', margin: 0 }}>av {result?.author_name}</p>}
        <span style={{ fontFamily: 'Syne, sans-serif', fontSize: '0.7rem', fontWeight: 700, color, letterSpacing: '0.08em', textTransform: 'uppercase' }}>LES MER →</span>
      </div>
    </a>
  );
};

const SearchResultsPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location?.search);
  const initialQuery = params?.get('q') || '';

  const [query, setQuery] = useState(initialQuery);
  const [inputValue, setInputValue] = useState(initialQuery);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState({ blog: [], legal: [], faq: [] });
  const [activeFilter, setActiveFilter] = useState('all');

  const searchContent = useCallback(async (q) => {
    if (!q?.trim()) { setResults({ blog: [], legal: [], faq: [] }); return; }
    setLoading(true);
    try {
      // Search blog posts
      const { data: blogData } = await supabase?.from('blog_posts')?.select('id, title, slug, excerpt, content, category, author_name, published_at, hero_image, hero_image_alt')?.or(`title.ilike.%${q}%,excerpt.ilike.%${q}%,content.ilike.%${q}%,category.ilike.%${q}%`)?.order('published_at', { ascending: false })?.limit(20);

      const blogResults = (blogData || [])?.map(p => ({ ...p, href: `/individual-article-detail-view?slug=${p?.slug}` }));

      // Search legal pages
      const legalResults = LEGAL_PAGES?.filter(p =>
        p?.title?.toLowerCase()?.includes(q?.toLowerCase()) ||
        p?.excerpt?.toLowerCase()?.includes(q?.toLowerCase()) ||
        p?.tags?.some(t => t?.toLowerCase()?.includes(q?.toLowerCase()))
      );

      // Search FAQ
      const faqResults = FAQ_DATA?.filter(f =>
        f?.title?.toLowerCase()?.includes(q?.toLowerCase()) ||
        f?.excerpt?.toLowerCase()?.includes(q?.toLowerCase()) ||
        f?.tags?.some(t => t?.toLowerCase()?.includes(q?.toLowerCase()))
      );

      setResults({ blog: blogResults, legal: legalResults, faq: faqResults });
    } catch (err) {
      console.error('Search error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (initialQuery) searchContent(initialQuery); }, []);

  const handleSearch = (e) => {
    e?.preventDefault();
    const q = inputValue?.trim();
    setQuery(q);
    navigate(`/search-results-page?q=${encodeURIComponent(q)}`, { replace: true });
    searchContent(q);
  };

  const totalResults = results?.blog?.length + results?.legal?.length + results?.faq?.length;
  const filters = [
    { key: 'all', label: 'Alle', count: totalResults },
    { key: 'blog', label: 'Blogg', count: results?.blog?.length },
    { key: 'legal', label: 'Juridisk', count: results?.legal?.length },
    { key: 'faq', label: 'FAQ', count: results?.faq?.length }
  ];

  const visibleBlog = activeFilter === 'all' || activeFilter === 'blog' ? results?.blog : [];
  const visibleLegal = activeFilter === 'all' || activeFilter === 'legal' ? results?.legal : [];
  const visibleFaq = activeFilter === 'all' || activeFilter === 'faq' ? results?.faq : [];

  return (
    <>
      <Helmet>
        <title>{query ? `Søk: "${query}" — NordPass` : 'Søk — NordPass'}</title>
        <meta name="description" content={`Søkeresultater for "${query}" på NordPass`} />
        <link href="https://fonts.googleapis.com/css2?family=Syne:wght@400;600;700;800&family=Inter:wght@300;400;500;600&family=JetBrains+Mono:wght@400;700&display=swap" rel="stylesheet" />
      </Helmet>
      <div style={{ background: '#050A12', minHeight: '100vh', overflowX: 'hidden' }}>
        {/* Nav */}
        <nav style={{ position: 'sticky', top: 0, zIndex: 100, background: 'rgba(5,10,18,0.97)', backdropFilter: 'blur(16px)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '0 5%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: '72px' }}>
            <a href="/nord-pass-landing-page" style={{ display: 'flex', alignItems: 'center', gap: '10px', textDecoration: 'none' }}>
              <div style={{ width: '28px', height: '28px', background: '#3B6FD4', clipPath: 'polygon(0 0, calc(100% - 6px) 0, 100% 6px, 100% 100%, 6px 100%, 0 calc(100% - 6px))' }} />
              <span style={{ fontFamily: 'Syne, sans-serif', fontSize: '1rem', fontWeight: 800, color: '#EEF0F2', letterSpacing: '-0.03em' }}>NordPass</span>
            </a>
            <a href="/blog-news-landing-page-blogg-nyheter" style={{ fontFamily: 'Inter, sans-serif', fontSize: '0.8rem', color: 'rgba(238,240,242,0.5)', textDecoration: 'none' }}>← Tilbake til Blogg</a>
          </div>
        </nav>

        {/* Hero search section */}
        <div style={{ background: '#0A1520', borderBottom: '1px solid rgba(255,255,255,0.06)', padding: '64px 5% 48px', position: 'relative', overflow: 'hidden' }}>
          <div style={{ position: 'absolute', top: '-40px', right: '5%', fontFamily: 'Impact, sans-serif', fontSize: '12rem', color: 'rgba(255,255,255,0.015)', letterSpacing: '-0.04em', lineHeight: 1, pointerEvents: 'none', userSelect: 'none' }}>SØK</div>
          <div style={{ maxWidth: '800px', margin: '0 auto', position: 'relative' }}>
            <p style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.65rem', color: '#3B6FD4', letterSpacing: '0.25em', textTransform: 'uppercase', margin: '0 0 12px' }}>SØKERESULTATER</p>
            {query && <h1 style={{ fontFamily: 'Impact, sans-serif', fontSize: '2.5rem', color: '#EEF0F2', letterSpacing: '-0.02em', textTransform: 'uppercase', margin: '0 0 8px' }}>"<span style={{ color: '#3B6FD4' }}>{query}</span>"</h1>}
            {query && !loading && <p style={{ fontFamily: 'Georgia, serif', fontStyle: 'italic', color: 'rgba(238,240,242,0.4)', fontSize: '0.9rem', margin: '0 0 32px' }}>{totalResults} resultat{totalResults !== 1 ? 'er' : ''} funnet</p>}

            {/* Search form */}
            <form onSubmit={handleSearch} style={{ display: 'flex', gap: '0', marginBottom: '0' }}>
              <input
                type="text"
                value={inputValue}
                onChange={e => setInputValue(e?.target?.value)}
                placeholder="Søk i artikler, juridiske sider, FAQ..."
                style={{ flex: 1, background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)', borderRight: 'none', padding: '16px 20px', fontFamily: 'Inter, sans-serif', fontSize: '1rem', color: '#EEF0F2', outline: 'none' }}
              />
              <button type="submit" style={{ background: '#3B6FD4', border: 'none', padding: '16px 28px', cursor: 'pointer', fontFamily: 'Syne, sans-serif', fontSize: '0.8rem', fontWeight: 700, color: '#EEF0F2', letterSpacing: '0.1em', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>SØK →</button>
            </form>
          </div>
        </div>

        {/* Results area */}
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '48px 5%' }}>
          {query && !loading && totalResults > 0 && (
            <div style={{ display: 'grid', gridTemplateColumns: '200px 1fr', gap: '40px', alignItems: 'start' }}>
              {/* Sidebar filters */}
              <div style={{ position: 'sticky', top: '88px' }}>
                <div style={{ background: '#0D1520', border: '1px solid rgba(255,255,255,0.06)', padding: '20px', transform: 'rotate(-0.4deg)' }}>
                  <p style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.6rem', color: '#3B6FD4', letterSpacing: '0.2em', textTransform: 'uppercase', margin: '0 0 16px' }}>INNHOLDSTYPE</p>
                  {filters?.map(f => (
                    <button key={f?.key} onClick={() => setActiveFilter(f?.key)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', background: activeFilter === f?.key ? 'rgba(59,111,212,0.1)' : 'none', border: activeFilter === f?.key ? '1px solid rgba(59,111,212,0.3)' : '1px solid transparent', padding: '10px 12px', cursor: 'pointer', marginBottom: '4px', transition: 'all 0.15s' }}>
                      <span style={{ fontFamily: 'Syne, sans-serif', fontSize: '0.8rem', fontWeight: activeFilter === f?.key ? 700 : 400, color: activeFilter === f?.key ? '#EEF0F2' : 'rgba(238,240,242,0.5)', letterSpacing: '0.03em' }}>{f?.label}</span>
                      <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.65rem', color: activeFilter === f?.key ? '#3B6FD4' : 'rgba(138,149,163,0.4)', letterSpacing: '0.05em' }}>{f?.count}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Results */}
              <div>
                {/* Blog results */}
                {visibleBlog?.length > 0 && (
                  <div style={{ marginBottom: '40px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px', borderBottom: '1px solid rgba(59,111,212,0.2)', paddingBottom: '12px' }}>
                      <h2 style={{ fontFamily: 'Impact, sans-serif', fontSize: '1.25rem', color: '#3B6FD4', letterSpacing: '0.05em', textTransform: 'uppercase', margin: 0 }}>BLOGG</h2>
                      <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.65rem', color: 'rgba(59,111,212,0.7)', border: '1px solid rgba(59,111,212,0.3)', padding: '2px 8px' }}>{visibleBlog?.length}</span>
                    </div>
                    {visibleBlog?.map(r => <ResultCard key={r?.id} result={r} query={query} type="Blog" />)}
                  </div>
                )}

                {/* Legal results */}
                {visibleLegal?.length > 0 && (
                  <div style={{ marginBottom: '40px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px', borderBottom: '1px solid rgba(249,115,22,0.2)', paddingBottom: '12px' }}>
                      <h2 style={{ fontFamily: 'Impact, sans-serif', fontSize: '1.25rem', color: '#F97316', letterSpacing: '0.05em', textTransform: 'uppercase', margin: 0 }}>JURIDISK</h2>
                      <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.65rem', color: 'rgba(249,115,22,0.7)', border: '1px solid rgba(249,115,22,0.3)', padding: '2px 8px' }}>{visibleLegal?.length}</span>
                    </div>
                    {visibleLegal?.map(r => <ResultCard key={r?.id} result={r} query={query} type="Juridisk" />)}
                  </div>
                )}

                {/* FAQ results */}
                {visibleFaq?.length > 0 && (
                  <div style={{ marginBottom: '40px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px', borderBottom: '1px solid rgba(46,196,160,0.2)', paddingBottom: '12px' }}>
                      <h2 style={{ fontFamily: 'Impact, sans-serif', fontSize: '1.25rem', color: '#2EC4A0', letterSpacing: '0.05em', textTransform: 'uppercase', margin: 0 }}>FAQ</h2>
                      <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.65rem', color: 'rgba(46,196,160,0.7)', border: '1px solid rgba(46,196,160,0.3)', padding: '2px 8px' }}>{visibleFaq?.length}</span>
                    </div>
                    {visibleFaq?.map(r => <ResultCard key={r?.id} result={r} query={query} type="FAQ" />)}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* No results */}
          {query && !loading && totalResults === 0 && (
            <div style={{ textAlign: 'center', padding: '80px 0' }}>
              <div style={{ fontFamily: 'Impact, sans-serif', fontSize: '6rem', color: 'rgba(255,255,255,0.03)', letterSpacing: '-0.04em', lineHeight: 1, marginBottom: '24px' }}>0</div>
              <p style={{ fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: '1.25rem', color: 'rgba(238,240,242,0.4)', letterSpacing: '0.05em', textTransform: 'uppercase', margin: '0 0 8px' }}>INGEN RESULTATER</p>
              <p style={{ fontFamily: 'Georgia, serif', fontStyle: 'italic', color: 'rgba(238,240,242,0.25)', fontSize: '0.9rem' }}>Prøv et annet søkeord eller bla gjennom kategoriene.</p>
            </div>
          )}

          {/* Loading */}
          {loading && (
            <div style={{ textAlign: 'center', padding: '80px 0' }}>
              <p style={{ fontFamily: 'Impact, sans-serif', fontSize: '2rem', color: 'rgba(255,255,255,0.06)', letterSpacing: '0.1em' }}>SØKER...</p>
            </div>
          )}

          {/* Empty state (no query) */}
          {!query && !loading && (
            <div style={{ textAlign: 'center', padding: '80px 0' }}>
              <p style={{ fontFamily: 'Impact, sans-serif', fontSize: '3rem', color: 'rgba(255,255,255,0.04)', letterSpacing: '-0.02em', textTransform: 'uppercase', margin: '0 0 16px' }}>SØKEMOTOR</p>
              <p style={{ fontFamily: 'Georgia, serif', fontStyle: 'italic', color: 'rgba(238,240,242,0.25)', fontSize: '0.9rem' }}>Skriv inn et søkeord for å finne artikler, juridiske sider og FAQ.</p>
            </div>
          )}
        </div>

        <LandingFooter />
      </div>
    </>
  );
};

export default SearchResultsPage;
